import { ArrowDown, Columns2, Landmark } from "lucide-react";

import { SectionIntro } from "@/components/SectionIntro";
import { pickText } from "@/lib/content";

const flowSteps = {
  en: [
    { title: "Crown", description: "Will, source, and macrocosmic contact." },
    { title: "Heart", description: "Integration, beauty, and ethical proportion." },
    { title: "Foundation", description: "Generation, transmission, and blueprinting." },
    { title: "Kingdom", description: "Embodiment, manifestation, and grounded output." },
  ],
  it: [
    { title: "Corona", description: "Volontà, sorgente e contatto macrocosmico." },
    { title: "Cuore", description: "Integrazione, bellezza e proporzione etica." },
    { title: "Fondamento", description: "Generazione, trasmissione e progettazione." },
    { title: "Regno", description: "Incarnazione, manifestazione e output radicato." },
  ],
};

export default function StructurePage({ language, summary }) {
  return (
    <div className="space-y-12" data-testid="structure-page">
      <SectionIntro
        description={pickText(
          language,
          "Anatomia Occulti reads the body through architectural order. Pillars regulate forces, zones divide function, and the central axis keeps the whole system coherent.",
          "Anatomia Occulti legge il corpo attraverso un ordine architettonico. Le colonne regolano le forze, le zone dividono la funzione e l'asse centrale mantiene coerente l'intero sistema.",
        )}
        eyebrow={pickText(language, "System Architecture", "Architettura del Sistema")}
        testId="structure-intro"
        title={pickText(language, "How the temple is assembled.", "Come viene assemblato il tempio.")}
      />

      <section className="grid gap-6 lg:grid-cols-[1fr_1.1fr]" data-testid="structure-pillars-section">
        <div className="glass-card rounded-[2rem] p-6 md:p-8">
          <div className="flex items-center gap-3 text-primary" data-testid="structure-pillars-header">
            <Columns2 className="h-5 w-5" />
            <p className="section-label">{pickText(language, "The Pillars", "Le Colonne")}</p>
          </div>
          <div className="mt-6 space-y-4" data-testid="structure-pillars-list">
            {(summary?.pillars ?? []).map((pillar) => (
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid={`structure-pillar-card-${pillar.slug}`} key={pillar.id}>
                <h3 className="text-3xl text-foreground" data-testid={`structure-pillar-title-${pillar.slug}`}>
                  {pickText(language, pillar.title_en, pillar.title_it)}
                </h3>
                <p className="mt-3 text-sm leading-7 text-slate-300" data-testid={`structure-pillar-description-${pillar.slug}`}>
                  {pickText(language, pillar.description_en, pillar.description_it)}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card rounded-[2rem] p-6 md:p-8">
          <div className="flex items-center gap-3 text-primary" data-testid="structure-zones-header">
            <Landmark className="h-5 w-5" />
            <p className="section-label">{pickText(language, "The Zones", "Le Zone")}</p>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="structure-zones-grid">
            {(summary?.zones ?? []).map((zone) => (
              <div className="rounded-3xl border border-white/10 bg-black/20 p-5" data-testid={`structure-zone-card-${zone.slug}`} key={zone.id}>
                <p className="section-label" data-testid={`structure-zone-title-${zone.slug}`}>
                  {pickText(language, zone.title_en, zone.title_it)}
                </p>
                <p className="mt-4 text-sm leading-7 text-slate-300" data-testid={`structure-zone-description-${zone.slug}`}>
                  {pickText(language, zone.description_en, zone.description_it)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="glass-card rounded-[2rem] p-6 md:p-8" data-testid="structure-flow-section">
        <div className="max-w-3xl space-y-3">
          <p className="section-label" data-testid="structure-flow-eyebrow">
            {pickText(language, "Operational Flow", "Flusso Operativo")}
          </p>
          <h3 className="text-4xl text-foreground" data-testid="structure-flow-title">
            {pickText(language, "Descent, harmonization, generation, manifestation.", "Discesa, armonizzazione, generazione, manifestazione.")}
          </h3>
        </div>
        <div className="mt-8 grid gap-4 lg:grid-cols-4" data-testid="structure-flow-grid">
          {flowSteps[language].map((step, index) => (
            <div className="rounded-3xl border border-white/10 bg-black/20 p-5" data-testid={`structure-flow-card-${index}`} key={step.title}>
              <p className="section-label" data-testid={`structure-flow-step-${index}`}>
                0{index + 1}
              </p>
              <h4 className="mt-4 text-3xl text-foreground" data-testid={`structure-flow-title-${index}`}>
                {step.title}
              </h4>
              <p className="mt-3 text-sm leading-7 text-slate-300" data-testid={`structure-flow-description-${index}`}>
                {step.description}
              </p>
              {index < flowSteps[language].length - 1 ? <ArrowDown className="mt-5 h-5 w-5 text-primary" /> : null}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}